// todo: add a button to export the events to a csv file

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Routes, Route } from 'react-router-dom';
import './App.css';
import Footer from './components/Footer';
import PrimaryTicketDisplay from './components/PrimaryTicketDisplay';
import ActiveTicketsTable from './components/ActiveTicketsTable';
import AccessGate from './components/AccessGate';
import Lightbox from './components/Lightbox';
import Report from './components/Report';
import Info from './components/Info';
import Admin from './components/Admin';
import { fetchApiData, flushTestTables, flushProductionTables } from './services/apiService';
import { processTicketLifecycle } from './services/ticketService';
import { config } from './config';
import { logEnvironmentState, clearAndonXStorage } from './utils/environmentHelper';


function Dashboard() {
  const [environment, setEnvironment] = useState('production');
  const [apiResponse, setApiResponse] = useState(null);
  const [dbLog, setDbLog] = useState([]);
  const [activeTickets, setActiveTickets] = useState([]);
  const [ticketEvents, setTicketEvents] = useState([]);
  const [mostRecentTicket, setMostRecentTicket] = useState(null);
  const [lightboxType, setLightboxType] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [timerProgress, setTimerProgress] = useState(0);
  const [partDetails, setPartDetails] = useState(null);
  const [partDetailsError, setPartDetailsError] = useState(null);
  const [environmentChangeNotification, setEnvironmentChangeNotification] = useState(null);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [hasInitialFetch, setHasInitialFetch] = useState(false);
  const timerRef = React.useRef();
  const slideshowRef = React.useRef();

  // Load environment from localStorage or use defaults
  useEffect(() => {
    const savedEnvironment = localStorage.getItem('andonx_environment');

    if (savedEnvironment) {
      setEnvironment(savedEnvironment);
    } else {
      // Use default environment from config
      setEnvironment(config.environment);
    }
  }, []);

  // Auto-fetch on initial load
  useEffect(() => {
    // Only auto-fetch if we have an environment set, no existing data, and haven't done initial fetch yet
    if (environment && !apiResponse && !isLoading && !hasInitialFetch) {
      // Auto-fetching data on initial load
      setHasInitialFetch(true);
      handleAutoFetch();
    }
  }, [environment, apiResponse, isLoading, hasInitialFetch]); // Depend on environment to trigger when it's loaded

  // Save environment to localStorage when it changes
  useEffect(() => {
    if (environment) {
      localStorage.setItem('andonx_environment', environment);
      // Environment saved to localStorage
    }
  }, [environment]);

  // Clear state when environment changes to prevent mixing test/production data
  useEffect(() => {
    // Environment changed - clearing previous data

    // Show notification about environment change
    setEnvironmentChangeNotification(`Switched to ${environment.toUpperCase()} environment - clearing previous data`);

    // Clear all state
    setApiResponse(null);
    setActiveTickets([]);
    setTicketEvents([]);
    setMostRecentTicket(null);
    setPartDetails(null);
    setPartDetailsError(null);
    setError(null);
    setTimerProgress(0);
    setCurrentSlideIndex(0); // Reset slide index when environment changes
    setHasInitialFetch(false); // Reset initial fetch flag to allow auto-fetch in new environment

    // Clear notification after 3 seconds
    setTimeout(() => {
      setEnvironmentChangeNotification(null);
    }, 3000);
  }, [environment]);

  // Debug: Log environment state on mount
  useEffect(() => {
    logEnvironmentState();
  }, []);

  const logDbOperation = useCallback((operation, data = null, result = null) => {
    const now = new Date();
    const pacificTime = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Los_Angeles',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    }).format(now);

    const logEntry = {
      timestamp: pacificTime,
      operation,
      data,
      result
    };

    setDbLog(prev => [...prev, logEntry]);
  }, []);



  const handleFetchData = async (e) => {
    e.preventDefault();

    setIsLoading(true);
    setError(null);

    try {
      logDbOperation('API Request Started', {
        environment,
        endpoint: 'status-entries',
        apiKeyLength: config.apiKey.length
      });

      const response = await fetchApiData(config.apiKey, environment, 'status-entries');

      if (response.error) {
        setError(response.error);
        logDbOperation('API Request Failed', {
          error: response.error,
          httpCode: response.http_code
        }, false);
        return;
      }

      setApiResponse(response);
      logDbOperation('API Request Successful', {
        httpCode: response.http_code,
        recordCount: response.response?.length || 0
      }, true);

      // Process ticket lifecycle if we have response data
      if (response.response && Array.isArray(response.response)) {
        const result = await processTicketLifecycle(
          response.response,
          environment,
          logDbOperation,
          config.apiKey
        );

        const { tickets, events } = result;
        setActiveTickets(tickets);
        setTicketEvents(events);
        setCurrentSlideIndex(0); // Reset to first ticket when new data arrives

        // Get most recent ticket for primary display
        if (tickets.length > 0) {
          // Find the ticket whose latest event has the most recent event_date
          const ticketWithLatestEvent = tickets.reduce((latest, ticket) => {
            if (!ticket.events || ticket.events.length === 0) return latest;
            // Find the event with the latest event_date for this ticket
            const ticketLatestEvent = ticket.events.reduce((a, b) =>
              new Date(a.event_date) > new Date(b.event_date) ? a : b
            );
            if (!latest) return ticket;
            const latestEvent = latest.events.reduce((a, b) =>
              new Date(a.event_date) > new Date(b.event_date) ? a : b
            );
            return new Date(ticketLatestEvent.event_date) > new Date(latestEvent.event_date) ? ticket : latest;
          }, null);
          setMostRecentTicket(ticketWithLatestEvent);
        }
      }

    } catch (err) {
      setError(`Request failed: ${err.message}`);
      logDbOperation('API Request Exception', {
        error: err.message
      }, false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFlushTestTables = async () => {
    if (environment !== 'test') {
      setError('Flush operation is only allowed in test environment');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      logDbOperation('Flush Test Tables Started', { environment });

      const result = await flushTestTables(environment);

      if (result.success) {
        logDbOperation('Flush Test Tables Successful', {
          message: result.message,
          tablesAffected: result.tables_affected
        }, true);

        // Clear local state
        setActiveTickets([]);
        setTicketEvents([]);
        setMostRecentTicket(null);
        setApiResponse(null);
        setCurrentSlideIndex(0); // Reset slide index when data is cleared
      } else {
        setError(result.error);
        logDbOperation('Flush Test Tables Failed', {
          error: result.error
        }, false);
      }
    } catch (err) {
      setError(`Flush failed: ${err.message}`);
      logDbOperation('Flush Test Tables Exception', {
        error: err.message
      }, false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFlushProductionTables = async () => {
    if (environment !== 'production') {
      setError('Flush operation is only allowed in production environment');
      return;
    }

    // Single confirmation for production
    if (!window.confirm('⚠️ WARNING: Are you sure you want to flush all PRODUCTION tables? This will delete ALL production data and cannot be undone!')) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      logDbOperation('Flush Production Tables Started', { environment });

      const result = await flushProductionTables(environment);

      if (result.success) {
        logDbOperation('Flush Production Tables Successful', {
          message: result.message,
          tablesAffected: result.tables_affected
        }, true);

        // Clear local state
        setActiveTickets([]);
        setTicketEvents([]);
        setMostRecentTicket(null);
        setApiResponse(null);
        setPartDetails(null);
        setPartDetailsError(null);
        setCurrentSlideIndex(0); // Reset slide index when data is cleared

        alert('Production tables flushed successfully!');
      } else {
        const errorMessage = result.error || 'Unknown error occurred';
        console.error('Flush production tables failed:', result);
        setError(`Flush failed: ${errorMessage}`);
        logDbOperation('Flush Production Tables Failed', {
          error: errorMessage,
          details: result
        }, false);
      }
    } catch (err) {
      setError(`Flush failed: ${err.message}`);
      logDbOperation('Flush Production Tables Exception', {
        error: err.message
      }, false);
    } finally {
      setIsLoading(false);
    }
  };

  const openLightbox = (type) => {
    setLightboxType(type);
  };

  const closeLightbox = () => {
    setLightboxType(null);
  };

  const handlePartDetailsUpdate = (details, error) => {
    setPartDetails(details);
    setPartDetailsError(error);
  };

  const getStatusText = () => {
    if (isLoading) {
      return `Loading... | Endpoint: status-entries`;
    }
    if (apiResponse) {
      return `Active | Endpoint: status-entries`;
    }
    return `Ready | Endpoint: status-entries`;
  };

  const getStatusClass = () => {
    if (isLoading) return 'status-warning';
    if (apiResponse) return 'status-success';
    return 'status-neutral';
  };

  // Timer effect: increments every second, triggers fetch at 60
  useEffect(() => {
    if (isLoading) return; // Pause timer while loading

    timerRef.current = setInterval(() => {
      setTimerProgress(prev => {
        if (prev >= 59) {
          // Trigger fetch and reset timer
          handleAutoFetch();
          return 0;
        }
        return prev + 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [isLoading, environment]);

  // Get filtered tickets for carousel (excludes resolved tickets older than 10 minutes)
  // Memoized so it only recalculates when activeTickets changes
  const carouselTickets = useMemo(() => {
    if (!activeTickets || activeTickets.length === 0) return [];

    const alertStatuses = ['Alerted', 'Acknowledged', 'Evaluated'];
    const now = new Date();

    return activeTickets.filter(ticket => {
      // Keep all active/open tickets - they are ALWAYS included regardless of age
      // This ensures tickets that are still open after 24+ hours remain in the carousel
      if (ticket.is_active) return true;

      // For resolved tickets, check if resolution is within 10 minutes
      if (ticket.events && ticket.events.length > 0) {
        // Find the latest event
        const latestEvent = ticket.events.reduce((a, b) =>
          new Date(a.event_date) > new Date(b.event_date) ? a : b
        );

        // Check if this is a resolved ticket
        const isLatestEventNonAlert = !alertStatuses.some(alertStatus =>
          latestEvent.status.toLowerCase() === alertStatus.toLowerCase()
        );

        if (isLatestEventNonAlert) {
          // Check if the resolved event is within 10 minutes
          const resolvedTime = new Date(latestEvent.event_date);
          const minutesSinceResolved = (now - resolvedTime) / (1000 * 60);

          // Include if resolved within 10 minutes, exclude if older
          return minutesSinceResolved <= 10;
        }
      }

      // Include tickets that don't match resolved criteria
      return true;
    });
  }, [activeTickets]);

  // Reset slide index when carousel tickets change
  useEffect(() => {
    if (carouselTickets && carouselTickets.length > 0) {
      // Ensure currentSlideIndex is within bounds
      if (currentSlideIndex >= carouselTickets.length) {
        setCurrentSlideIndex(0);
      }
    } else {
      setCurrentSlideIndex(0);
    }
  }, [carouselTickets]);

  // Slideshow effect: cycles through carousel tickets every 10 seconds
  useEffect(() => {
    if (!carouselTickets || carouselTickets.length === 0) {
      return;
    }

    slideshowRef.current = setInterval(() => {
      setCurrentSlideIndex(prev => {
        const nextIndex = (prev + 1) % carouselTickets.length;
        return nextIndex;
      });
    }, 10000); // 10 seconds

    return () => clearInterval(slideshowRef.current);
  }, [carouselTickets]);

  // Get current primary ticket for display (from carousel tickets)
  const getCurrentPrimaryTicket = () => {
    if (!carouselTickets || carouselTickets.length === 0) return null;
    return carouselTickets[currentSlideIndex] || carouselTickets[0];
  };

  // Manual timer click handler (immediate fetch)
  const handleTimerClick = () => {
    setTimerProgress(0);
    handleAutoFetch();
  };

  // Auto-fetch logic (same as handleFetchData, but no event)
  const handleAutoFetch = async () => {
    // Auto-fetch starting

    setIsLoading(true);
    setError(null);
    try {
      logDbOperation('API Auto-Request Started', {
        environment,
        endpoint: 'status-entries',
        apiKeyLength: config.apiKey.length
      });
      const response = await fetchApiData(config.apiKey, environment, 'status-entries');
      if (response.error) {
        setError(response.error);
        logDbOperation('API Auto-Request Failed', {
          error: response.error,
          httpCode: response.http_code
        }, false);
        return;
      }
      setApiResponse(response);
      logDbOperation('API Auto-Request Successful', {
        httpCode: response.http_code,
        recordCount: response.response?.length || 0
      }, true);
      if (response.response && Array.isArray(response.response)) {
        const result = await processTicketLifecycle(
          response.response,
          environment,
          logDbOperation,
          config.apiKey
        );
        const { tickets, events } = result;
        setActiveTickets(tickets);
        setTicketEvents(events);
        setCurrentSlideIndex(0); // Reset to first ticket when new data arrives

        // Get most recent ticket for primary display
        if (tickets.length > 0) {
          // Find the ticket whose latest event has the most recent event_date
          const ticketWithLatestEvent = tickets.reduce((latest, ticket) => {
            if (!ticket.events || ticket.events.length === 0) return latest;
            // Find the event with the latest event_date for this ticket
            const ticketLatestEvent = ticket.events.reduce((a, b) =>
              new Date(a.event_date) > new Date(b.event_date) ? a : b
            );
            if (!latest) return ticket;
            const latestEvent = latest.events.reduce((a, b) =>
              new Date(a.event_date) > new Date(b.event_date) ? a : b
            );
            return new Date(ticketLatestEvent.event_date) > new Date(latestEvent.event_date) ? ticket : latest;
          }, null);

          // Simple ticket count logging for auto-fetch
          // Cycling through tickets

          setMostRecentTicket(ticketWithLatestEvent);
        }
      }
    } catch (err) {
      setError(`Request failed: ${err.message}`);
      logDbOperation('API Auto-Request Exception', {
        error: err.message
      }, false);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AccessGate>
      <div className="App">
        {/* Primary Ticket Display */}
        {apiResponse && (
          <div className="primary-ticket-display">
            <PrimaryTicketDisplay
              ticket={getCurrentPrimaryTicket()}
              activeTickets={carouselTickets}
              currentSlideIndex={currentSlideIndex}
              apiKey={config.apiKey}
              environment={environment}
              apiResponse={apiResponse}
              onPartDetailsUpdate={handlePartDetailsUpdate}
            />
          </div>
        )}

        {/* Environment Change Notification */}
        {environmentChangeNotification && (
          <div className="environment-notification" style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            backgroundColor: '#17a2b8',
            color: 'white',
            padding: '12px 20px',
            borderRadius: '6px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 1000,
            fontSize: '14px',
            fontWeight: '500'
          }}>
            <i className="fas fa-info-circle" style={{ marginRight: '8px' }}></i>
            {environmentChangeNotification}
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="error-container">
            <div className="error">{error}</div>
          </div>
        )}

        {/* Active Tickets Table */}
        {apiResponse && (
          <ActiveTicketsTable activeTickets={activeTickets} primaryTicket={getCurrentPrimaryTicket()} />
        )}

        {/* Footer */}
        <Footer
          onOpenLightbox={openLightbox}
          timerProgress={timerProgress}
          onTimerClick={handleTimerClick}
          environment={environment}
        />

        {/* Lightbox */}
        <Lightbox
          type={lightboxType}
          onClose={closeLightbox}
          apiResponse={apiResponse}
          dbLog={dbLog}
          activeTickets={activeTickets}
          ticketEvents={ticketEvents}
          environment={environment}
          setEnvironment={setEnvironment}
          onFetchData={handleFetchData}
          isLoading={isLoading}
          partDetails={partDetails}
          partDetailsError={partDetailsError}
          onOpenLightbox={openLightbox}
        />
      </div>
    </AccessGate>
  );
}

function App() {
  return (
    <Routes>
      <Route path="/" element={<Dashboard />} />
      <Route path="/report" element={<Report />} />
      <Route path="/info" element={<Info />} />
      <Route path="/admin" element={<Admin />} />
    </Routes>
  );
}

export default App;
